import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axios } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function EnumeratorStats() {
  const navigate = useNavigate();
  const [stats, setStats]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    axios.get('/enumerator-stats')
      .then(r => setStats(r.data))
      .catch(() => toast.error('Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  const loadDetail = async (id, name) => {
    setSelected({ id, name });
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`/enumerator-stats/${id}`);
      setDetail(data);
    } catch { toast.error('Failed to load activity'); }
    finally { setLoadingDetail(false); }
  };

  const fmt = ts => new Date(String(ts).replace(' ','T')+(String(ts).includes('Z')?'':'Z')).toLocaleDateString('en-IN', { timeZone:'Asia/Kolkata', day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });

  if (loading) return <div className="loader"><div className="spinner"/></div>;

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(160deg,#FFF4ED,#FFF8F0)', fontFamily:"'Outfit',sans-serif" }}>

      {/* Header */}
      <div className="app-header">
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button className="back-btn" onClick={()=>navigate('/')}>←</button>
          <div>
            <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:'1.05rem', color:'white' }}>👥 Enumerator Activity</div>
            <div style={{ fontSize:'0.65rem', opacity:.82, color:'white' }}>{stats.length} enumerators</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:700, margin:'0 auto', padding:'16px 16px 80px' }}>

        {/* Summary Cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:18 }}>
          {[
            { l:'Total Added',   v:stats.reduce((s,e)=>s+parseInt(e.added||0),0),   c:'#2E7D32', bg:'#E8F5E9' },
            { l:'Total Edited',  v:stats.reduce((s,e)=>s+parseInt(e.edited||0),0),  c:'#1565C0', bg:'#E3F2FD' },
            { l:'Total Deleted', v:stats.reduce((s,e)=>s+parseInt(e.deleted||0),0), c:'#B71C1C', bg:'#FFEBEE' },
          ].map(s=>(
            <div key={s.l} style={{ background:s.bg, borderRadius:14, padding:'12px 10px', textAlign:'center', border:`1.5px solid ${s.c}22` }}>
              <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:800, fontSize:'1.4rem', color:s.c }}>{s.v}</div>
              <div style={{ fontSize:'0.68rem', color:'#666', fontWeight:600, marginTop:2 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Enumerator List */}
        {stats.length === 0
          ? <div style={{ textAlign:'center', padding:40, color:'#9E6040' }}>
              <div style={{ fontSize:'2.5rem', marginBottom:10 }}>👤</div>
              <div style={{ fontWeight:700 }}>No enumerators found</div>
            </div>
          : stats.map(e => (
            <div key={e.id} style={{ background:'rgba(255,255,255,0.92)', borderRadius:18, padding:'16px', marginBottom:12, border:'1.5px solid rgba(255,107,0,0.12)', boxShadow:'0 3px 14px rgba(0,0,0,0.05)' }}>
              {/* Top row */}
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                <div style={{ width:46, height:46, borderRadius:14, background:'linear-gradient(135deg,#E55100,#FF6B00)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:'1.1rem', flexShrink:0 }}>
                  {e.name?.charAt(0)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:'0.95rem', color:'#1A0A00' }}>{e.name}</div>
                  <div style={{ display:'flex', gap:6, marginTop:3, flexWrap:'wrap' }}>
                    {e.enumerator_id && <span style={{ background:'linear-gradient(135deg,#E55100,#FF6B00)', color:'white', padding:'2px 8px', borderRadius:99, fontSize:'0.68rem', fontWeight:700 }}>{e.enumerator_id}</span>}
                    <span style={{ background:e.is_active?'#E8F5E9':'#FFEBEE', color:e.is_active?'#2E7D32':'#C62828', padding:'2px 8px', borderRadius:99, fontSize:'0.68rem', fontWeight:600 }}>{e.is_active?'Active':'Inactive'}</span>
                    {e.phone && <span style={{ color:'#9E6040', fontSize:'0.72rem' }}>📱 {e.phone}</span>}
                  </div>
                </div>
                <button onClick={()=>loadDetail(e.id, e.name)} style={{ background:'rgba(255,107,0,0.1)', border:'1.5px solid rgba(255,107,0,0.25)', color:'#E55100', padding:'6px 12px', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:'0.75rem', flexShrink:0 }}>
                  View Logs
                </button>
              </div>

              {/* Stats row */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
                {[
                  { l:'Added',   v:parseInt(e.added||0),   c:'#2E7D32', bg:'#E8F5E9', icon:'✅' },
                  { l:'Edited',  v:parseInt(e.edited||0),  c:'#1565C0', bg:'#E3F2FD', icon:'✏️' },
                  { l:'Deleted', v:parseInt(e.deleted||0), c:'#B71C1C', bg:'#FFEBEE', icon:'🗑️' },
                  { l:'Last Active', v: e.last_active ? fmt(e.last_active) : 'Never', c:'#666', bg:'#F5F5F5', icon:'🕐', small:true },
                ].map(s=>(
                  <div key={s.l} style={{ background:s.bg, borderRadius:10, padding:'8px 6px', textAlign:'center' }}>
                    <div style={{ fontSize:'0.8rem' }}>{s.icon}</div>
                    <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:800, fontSize:s.small?'0.65rem':'1rem', color:s.c, lineHeight:1.2 }}>{s.v}</div>
                    <div style={{ fontSize:'0.6rem', color:'#888', marginTop:1 }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          ))
        }
      </div>

      {/* Detail Modal */}
      {selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={()=>setSelected(null)}>
          <div style={{ width:'100%', maxWidth:500, margin:'0 auto', background:'white', borderRadius:'20px 20px 0 0', padding:'20px 18px 40px', maxHeight:'75vh', overflow:'hidden', display:'flex', flexDirection:'column' }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:700, color:'#BF360C' }}>📋 {selected.name} — Logs</div>
              <button onClick={()=>setSelected(null)} style={{ background:'none', border:'none', fontSize:'1.3rem', cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ overflowY:'auto', flex:1 }}>
              {loadingDetail
                ? <div style={{ textAlign:'center', padding:24, color:'#FF6B00' }}>Loading...</div>
                : detail.length === 0
                  ? <div style={{ textAlign:'center', padding:24, color:'#9E6040' }}>No voter activity yet</div>
                  : detail.map((log,i) => (
                    <div key={i} style={{ display:'flex', gap:10, padding:'10px 0', borderBottom:'1px solid rgba(255,107,0,0.08)', alignItems:'flex-start' }}>
                      <div style={{ width:32, height:32, borderRadius:8, background:log.action_type==='ADD'?'#E8F5E9':log.action_type==='EDIT'?'#E3F2FD':'#FFEBEE', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.9rem', flexShrink:0 }}>
                        {log.action_type==='ADD'?'✅':log.action_type==='EDIT'?'✏️':'🗑️'}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:'0.85rem', color:'#1A0A00' }}>{log.description}</div>
                        <div style={{ fontSize:'0.72rem', color:'#9E6040', marginTop:2 }}>🕐 {fmt(log.created_at)}</div>
                      </div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}